import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BUSINESS, SERVICE_OPTIONS } from "@/data/site";
import Icon from "@/components/Icon";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const EMPTY = { name: "", phone: "", service: "", message: "" };
const inputCls =
    "w-full bg-elevated border border-ink/10 rounded-lg px-4 py-3 text-ink placeholder:text-ice/30 focus:border-cyan2 focus:ring-1 focus:ring-cyan2 outline-none transition-all duration-300";

function validate(f, msgs) {
    const e = {};
    if (f.name.trim().length < 2) e.name = msgs.name;
    if (!/^\+?[\d\s-]{10,15}$/.test(f.phone.trim()) || f.phone.replace(/\D/g, "").length < 10)
        e.phone = msgs.phone;
    if (!f.service) e.service = msgs.service;
    if (f.message.trim().length < 5) e.message = msgs.message;
    return e;
}

function MapFrame({ query, title, testid }) {
    return (
        <div data-testid={testid} className="rounded-2xl overflow-hidden border border-ink/8 h-56">
            <iframe
                title={title}
                src={`https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`}
                className="w-full h-full map-dark"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
            />
        </div>
    );
}

function AddressCard({ title, lines, gst, testid }) {
    return (
        <div data-testid={testid} className="flex items-start gap-5 rounded-2xl border border-ink/8 bg-surface p-6">
            <span className="w-14 h-14 rounded-2xl border border-solar/30 bg-solar/10 flex items-center justify-center shrink-0">
                <Icon name="mappin" size={24} className="text-solar" />
            </span>
            <div>
                <span className="block font-mono2 text-[11px] tracking-[0.25em] uppercase text-ice/45">{title}</span>
                <address className="not-italic mt-2 text-ice/80 leading-relaxed text-[15px]">
                    {lines.map((l) => (
                        <span key={l} className="block">{l}</span>
                    ))}
                </address>
                {gst && (
                    <p className="mt-3 font-mono2 text-[11px] tracking-[0.15em] text-ice/45" data-testid="contact-gst">
                        GSTIN: {gst}
                    </p>
                )}
            </div>
        </div>
    );
}

export default function Contact() {
    const { t } = useLang();
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        const onPreselect = (ev) => {
            setForm((f) => ({ ...f, service: ev.detail }));
            setSent(false);
        };
        window.addEventListener("preselect-service", onPreselect);
        return () => window.removeEventListener("preselect-service", onPreselect);
    }, []);

    const set = (k) => (ev) => {
        setForm((f) => ({ ...f, [k]: ev.target.value }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate(form, t.contact.errors);
        setErrors(e);
        if (Object.keys(e).length) return;
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            setSent(true);
            setForm(EMPTY);
            toast.success(t.contact.toastOk);
        } catch {
            toast.error(t.contact.toastErr);
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-[#F1EDE4]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Reveal>
                    <Eyebrow>{t.contact.eyebrow}</Eyebrow>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-ink mt-4">
                        {t.contact.heading}
                    </h2>
                </Reveal>

                <div className="mt-14 grid gap-10 lg:grid-cols-2">
                    <div className="space-y-6">
                        <Reveal>
                            <div
                                data-testid="contact-phone-card"
                                className="group flex items-center gap-5 rounded-2xl border border-ink/8 bg-surface p-6 hover:border-cyan2/40 transition-colors duration-300"
                            >
                                <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-electric to-cyan2 flex items-center justify-center shrink-0 group-hover:shadow-[0_0_30px_rgba(0,207,255,0.5)] transition-shadow duration-300">
                                    <Icon name="phone" size={24} className="text-ink" />
                                </span>
                                <span>
                                    <span className="block font-mono2 text-[11px] tracking-[0.25em] uppercase text-ice/45">{t.contact.phoneLabel}</span>
                                    <a
                                        href={BUSINESS.tel}
                                        data-testid="contact-phone-primary"
                                        className="block font-heading text-2xl font-bold text-ink mt-1 hover:text-cyan2 transition-colors"
                                    >
                                        {BUSINESS.phoneDisplay}
                                    </a>
                                    <span className="block text-sm text-ice/55 mt-1.5">
                                        {t.contact.alsoCall}{" "}
                                        {BUSINESS.extraPhones.map((p, i) => (
                                            <span key={p}>
                                                <a
                                                    data-testid={`contact-phone-alt-${i}`}
                                                    href={`tel:${p}`}
                                                    className="text-ice/80 hover:text-cyan2 transition-colors"
                                                >
                                                    {p}
                                                </a>
                                                {i < BUSINESS.extraPhones.length - 1 ? " · " : ""}
                                            </span>
                                        ))}
                                    </span>
                                </span>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <AddressCard
                                testid="contact-address-card"
                                title={`${t.contact.addressLabel} — ${t.contact.addr1Title}`}
                                lines={BUSINESS.addressLines}
                                gst={BUSINESS.gst}
                            />
                        </Reveal>
                        <Reveal delay={0.15}>
                            <MapFrame query={BUSINESS.mapQuery} title={t.contact.map1} testid="contact-map" />
                        </Reveal>
                        <Reveal delay={0.2}>
                            <AddressCard
                                testid="contact-address2-card"
                                title={`${t.contact.addressLabel} — ${t.contact.addr2Title}`}
                                lines={BUSINESS.address2Lines}
                            />
                        </Reveal>
                        <Reveal delay={0.25}>
                            <MapFrame query={BUSINESS.mapQuery2} title={t.contact.map2} testid="contact-map-2" />
                        </Reveal>
                    </div>

                    <Reveal delay={0.1}>
                        <form
                            data-testid="enquiry-form"
                            onSubmit={submit}
                            noValidate
                            className="rounded-2xl border border-ink/8 bg-surface p-7 md:p-9"
                        >
                            <h3 className="font-heading text-2xl font-semibold text-ink">
                                {t.contact.formTitle}
                            </h3>
                            <p className="mt-2 text-sm text-ice/55">
                                {t.contact.formSub}
                            </p>

                            {sent && (
                                <div data-testid="enquiry-success" className="mt-5 flex items-start gap-3 rounded-xl border border-cyan2/30 bg-cyan2/10 px-4 py-3.5 text-sm text-cyan2">
                                    <Icon name="check" size={18} className="shrink-0 mt-0.5" />
                                    {t.contact.success}
                                </div>
                            )}

                            <div className="mt-6 space-y-5">
                                <div>
                                    <label htmlFor="enquiry-name" className="block text-sm font-medium text-ice/80 mb-2">{t.contact.name}</label>
                                    <input
                                        id="enquiry-name"
                                        data-testid="enquiry-name-input"
                                        type="text"
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder={t.contact.namePh}
                                        className={inputCls}
                                        autoComplete="name"
                                    />
                                    {errors.name && <p data-testid="enquiry-name-error" className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                                </div>
                                <div>
                                    <label htmlFor="enquiry-phone" className="block text-sm font-medium text-ice/80 mb-2">{t.contact.phone}</label>
                                    <input
                                        id="enquiry-phone"
                                        data-testid="enquiry-phone-input"
                                        type="tel"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder={t.contact.phonePh}
                                        className={inputCls}
                                        autoComplete="tel"
                                    />
                                    {errors.phone && <p data-testid="enquiry-phone-error" className="mt-1.5 text-xs text-red-400">{errors.phone}</p>}
                                </div>
                                <div>
                                    <label htmlFor="enquiry-service" className="block text-sm font-medium text-ice/80 mb-2">{t.contact.service}</label>
                                    <select
                                        id="enquiry-service"
                                        data-testid="enquiry-service-select"
                                        value={form.service}
                                        onChange={set("service")}
                                        className={`${inputCls} ${form.service ? "" : "text-ice/30"}`}
                                    >
                                        <option value="" disabled>{t.contact.servicePh}</option>
                                        {SERVICE_OPTIONS.map((o) => (
                                            <option key={o} value={o} className="text-ink bg-elevated">{o}</option>
                                        ))}
                                    </select>
                                    {errors.service && <p data-testid="enquiry-service-error" className="mt-1.5 text-xs text-red-400">{errors.service}</p>}
                                </div>
                                <div>
                                    <label htmlFor="enquiry-message" className="block text-sm font-medium text-ice/80 mb-2">{t.contact.message}</label>
                                    <textarea
                                        id="enquiry-message"
                                        data-testid="enquiry-message-input"
                                        rows={4}
                                        value={form.message}
                                        onChange={set("message")}
                                        placeholder={t.contact.messagePh}
                                        className={`${inputCls} resize-none`}
                                    />
                                    {errors.message && <p data-testid="enquiry-message-error" className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
                                </div>
                                <button
                                    data-testid="enquiry-submit-button"
                                    type="submit"
                                    disabled={sending}
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-electric to-cyan2 px-8 py-4 font-bold text-ink hover:shadow-[0_0_40px_rgba(0,207,255,0.4)] hover:scale-[1.01] active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none transition-all duration-300"
                                >
                                    {sending ? t.contact.sending : t.contact.submit}
                                    {!sending && <Icon name="arrow" size={17} />}
                                </button>
                            </div>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
